/**
 * Lightweight callback store for passing selected items back from a modal
 * picker screen (e.g. newAssetGroupReassign) to the component that opened it
 * (e.g. MultiItemSelector via selectorModalUrl).
 *
 * Usage:
 *  1. Before navigating to the picker modal, call `multiItemSelectorStore.request(callback, initialItems)`.
 *  2. Inside the picker modal's onSubmit, call `multiItemSelectorStore.resolve(items)` before `router.back()`.
 */

type Item = { id: string; name: string };
type Callback = (items: Item[]) => void;

let pendingCallback: Callback | null = null;
let pendingInitialItems: Item[] = [];

export const multiItemSelectorStore = {
  /** Register the callback that will receive the selected items, along with the current selection. */
  request: (callback: Callback, initialItems: Item[] = []) => {
    pendingCallback = callback;
    pendingInitialItems = initialItems;
  },

  /** Read (and clear) the initial items — call once on modal mount. */
  popInitialItems: (): Item[] => {
    const items = pendingInitialItems;
    pendingInitialItems = [];
    return items;
  },

  /** Call from the picker modal to deliver the selected items and clear the callback. */
  resolve: (items: Item[]) => {
    if (pendingCallback) {
      pendingCallback(items);
      pendingCallback = null;
    }
  },

  /**
   * Deliver the current selection WITHOUT clearing the callback — for live
   * side-panel pickers that keep the opener in sync on every toggle (the panel
   * stays open). Use `clear()` when the panel closes.
   */
  notify: (items: Item[]) => {
    pendingCallback?.(items);
  },

  /** Discard a pending callback without delivering results (e.g. on cancel). */
  clear: () => {
    pendingCallback = null;
    pendingInitialItems = [];
  },
};
